import { Fragment } from 'react';

import { Text } from '@change/ds-core-ui';
import { Form } from '@change/ds-forms';
import { InsightsIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';
import { css, Global } from '@change-corgi/design-system-legacy/core';

import { StepActions, ToggleButton, ToggleButtonGroup } from '#src/app/pages/startAPetitionV2/shared/components';
import { Callout, CalloutBody } from '#src/app/shared/components/callout';

import { LocationField } from './components/LocationField';
import { REACH_OPTIONS } from './constants';
import { useLocation } from './hooks/useLocation';

const isLocal = (option: string | undefined) => option === 'local';

export const Location: React.FC = () => {
	const { translate } = useI18n();
	const { data, actions } = useLocation();

	return (
		<div className="container mx-auto mb-[88px] flex max-w-[620px] flex-col gap-6 px-5 py-8" data-qa="location-step">
			<Text as="h1" variant="heading" className="m-0">
				<Translate value="corgi.start_a_petition.location.heading" />
			</Text>

			<Global
				styles={css`
					.toggle-button-grid .toggle-button-container {
						display: grid;
						gap: 16px;
						grid-template-areas:
							'local'
							${isLocal(data.selectedReach) ? "'combobox'" : ''}
							'national'
							'global';
					}

					@media (min-width: 640px) {
						.toggle-button-grid .toggle-button-container {
							grid-template-areas:
								'local national global'
								${isLocal(data.selectedReach) ? "'combobox combobox combobox'" : ''};
						}
					}

					.local {
						grid-area: local;
					}

					.national {
						grid-area: national;
					}

					.global {
						grid-area: global;
					}

					.combobox {
						grid-area: combobox;
						width: 100%;
						max-width: 100%;
						justify-self: stretch;
					}
				`}
			/>

			<Form onSubmit={actions.submit}>
				<ToggleButtonGroup
					className="toggle-button-grid [&_.toggle-button-container]:md:grid-cols-3"
					ariaLabel={translate('corgi.start_a_petition.location.toggle_group_aria_label')}
					data-qa="reach-options"
					onChange={(_, value) => actions.handleReachChange(value)}
					value={data.selectedReach}
					name="reach"
					error={data.formErrors.reach}
				>
					{REACH_OPTIONS.map((option) => {
						return (
							<Fragment key={option.name}>
								<ToggleButton value={option.name} className={option.name} data-qa={`${option.name}-reach-button`}>
									<div className="flex flex-col items-center gap-2">
										<option.Icon
											size="large"
											className="text-icon-weaker"
											gradient={option.name === data.selectedReach}
										/>
										<Text className="mx-2" variant="body" fontWeight="bold">
											<Translate value={`corgi.sap.reach.options.${option.name}`} />
										</Text>
									</div>
								</ToggleButton>
								{option.name === 'local' && (
									<div
										key="location-field"
										className={cn('combobox', data.selectedReach === 'local' ? 'block' : 'hidden')}
									>
										{/* eslint-disable-next-line @change-corgi/require-data-qa-attribute */}
										<LocationField errorMessage={data.formErrors.location} onChange={actions.handleLocationChange} />
									</div>
								)}
							</Fragment>
						);
					})}
				</ToggleButtonGroup>

				{isLocal(data.selectedReach) && (
					<Callout icon={<InsightsIcon />} className="mt-5" data-testid="local-callout">
						<CalloutBody>
							<Translate value="corgi.start_a_petition.location.info" />
						</CalloutBody>
					</Callout>
				)}

				<StepActions type="form" onSecondaryAction={actions.back} className="mt-12" />
			</Form>
		</div>
	);
};
